import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { 
  Users, 
  Plus, 
  Edit3, 
  Trash2, 
  Shield, 
  UserCheck, 
  Eye,
  Settings,
  Key
} from 'lucide-react';

const UserManagementPage = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [addForm, setAddForm] = useState({
    email: '',
    full_name: '',
    role: 'viewer',
    password: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const roles = [
    { value: 'admin', label: 'Administrator', description: 'Full system access', icon: '👑' },
    { value: 'manager', label: 'Manager', description: 'Manage trucks & content', icon: '📊' },
    { value: 'editor', label: 'Editor', description: 'Edit trucks & content', icon: '✏️' },
    { value: 'viewer', label: 'Viewer', description: 'Read-only access', icon: '👁️' }
  ];

  const getRoleInfo = (role) => {
    return roles.find(r => r.value === role) || roles[3];
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addForm)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Success",
          description: `User created successfully. ${result.email_sent ? 'Welcome email sent.' : 'Please share login details manually.'}`,
          variant: "default"
        });
        
        setShowAddModal(false);
        setAddForm({ email: '', full_name: '', role: 'viewer', password: '' });
        fetchUsers();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "User deleted successfully",
          variant: "default"
        });
        fetchUsers();
      } else {
        throw new Error('Failed to delete user');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast({
        title: "Error",
        description: "New passwords don't match",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Password changed successfully",
          variant: "default"
        });
        
        setShowPasswordModal(false);
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-black text-gray-900 uppercase">User Management</h1>
              <p className="text-gray-600 mt-2">Manage user accounts and permissions</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPasswordModal(true)}
                className="bg-gray-600 text-white px-4 py-2 rounded font-bold hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <Key className="h-4 w-4" />
                Change Password
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-orange-600 text-white px-6 py-2 rounded font-bold hover:bg-orange-700 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add User
              </button>
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members ({users.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No users found</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 transition-colors"
              >
                Add First User
              </button>
            </div>
          ) : (
            <div className="p-6">
              <div className="space-y-4">
                {users.map((user) => {
                  const roleInfo = getRoleInfo(user.role);
                  return (
                    <div key={user.id} className="border border-gray-200 rounded p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-orange-100 rounded-full p-3">
                            <span className="text-2xl">{roleInfo.icon}</span>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{user.full_name || user.email}</h3>
                            <p className="text-gray-600">{user.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                                user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                                user.role === 'editor' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {roleInfo.label}
                              </span>
                              {user.is_active ? (
                                <span className="text-green-600 flex items-center gap-1 text-xs">
                                  <UserCheck className="h-3 w-3" />
                                  Active
                                </span>
                              ) : (
                                <span className="text-red-600 flex items-center gap-1 text-xs">
                                  <Eye className="h-3 w-3" />
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditingUser(user)}
                            className="bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 transition-colors flex items-center gap-1"
                          >
                            <Edit3 className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.full_name || user.email)}
                            className="bg-red-100 text-red-700 px-3 py-2 rounded hover:bg-red-200 transition-colors flex items-center gap-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      {/* Role Description */}
                      <div className="mt-3 pl-16">
                        <p className="text-sm text-gray-600">{roleInfo.description}</p>
                        <div className="text-xs text-gray-500 mt-1">
                          Created: {new Date(user.created_at).toLocaleDateString()}
                          {user.last_login && ` • Last login: ${new Date(user.last_login).toLocaleDateString()}`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 uppercase">Add New User</h2>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Email Address</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm(prev => ({...prev, email: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="user@lawmancommercials.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Full Name</label>
                <input
                  type="text"
                  value={addForm.full_name}
                  onChange={(e) => setAddForm(prev => ({...prev, full_name: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Role</label>
                <select
                  value={addForm.role}
                  onChange={(e) => setAddForm(prev => ({...prev, role: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.icon} {role.label} - {role.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                  Password (Optional)
                </label>
                <input
                  type="password"
                  value={addForm.password}
                  onChange={(e) => setAddForm(prev => ({...prev, password: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Leave empty for auto-generated password"
                />
                <p className="text-xs text-gray-500 mt-1">If empty, a temporary password will be generated and emailed to the user</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 text-white py-2 rounded hover:bg-orange-700 transition-colors"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 uppercase">Change Password</h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Current Password</label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(prev => ({...prev, current_password: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">New Password</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(prev => ({...prev, new_password: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm(prev => ({...prev, confirm_password: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 text-white py-2 rounded hover:bg-orange-700 transition-colors"
                >
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;